import { DateProgressItem } from '@components'
import { getPeriodPageUrl, getVATPageUrl } from '@constants'
import { isNYBusiness, isVATObliged, useBusinessContext, useDateRangeContext } from '@context'
import { useTheme } from '@hooks'
import { dashboardQueryKey, dashboardQueryOptions, fetchVatPeriod } from '@query'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useDashboardOverview } from '../hooks/useDashboardOverview'
import { WHEN_STACKED } from '../layout'
import { Card } from './shared/Card'

export const PeriodProgressCard: React.FC = () => {
  const [t] = useTranslation()
  const theme = useTheme()
  const { slug, data: business } = useBusinessContext()
  const { period_id: periodId, vat_period_id: vatPeriodId } = business ?? {}
  const { options } = useDateRangeContext()
  const { data: overview } = useDashboardOverview()

  const isNY = isNYBusiness(business)
  const periodThis = options.find(option => option.key === 'period-this')
  const vatCurrent = overview?.vat_year?.vat_periods.find(period => period.current)
  const showPeriod = !isNY && Boolean(periodId) && Boolean(periodThis?.date_from)
  const showVat = !isNY && isVATObliged(business) && Boolean(vatPeriodId)

  const vatQueryKey = dashboardQueryKey(slug, 'vatPeriod', 'active')
  const { data: vatPeriod } = useQuery({
    queryKey: vatQueryKey,
    queryFn: () => fetchVatPeriod({ slug, vatPeriodId: vatPeriodId as number }),
    ...{
      enabled: showVat && vatCurrent == null && Boolean(vatPeriodId),
      ...dashboardQueryOptions(slug)
    }
  })

  const vat = vatCurrent ?? vatPeriod
  if (!showPeriod && !vat) return null

  return (
    <FullWidthCard
      title={t('dashboard.periods.mainTitle')}
      tooltip={t('dashboard.periods.mainTooltip')}
    >
      <Body>
        {showPeriod && periodThis?.date_from && periodThis?.date_to && (
          <ProgressLink to={getPeriodPageUrl(slug, { id: periodId })}>
            <DateProgressItem
              appearance="flat"
              initialAnimation
              dateFrom={new Date(periodThis.date_from)}
              dateTo={new Date(periodThis.date_to)}
              titleFrom={t('dashboard.periodProgress.periodStart')}
              titleTo={t('dashboard.periodProgress.periodEnd')}
              progressColor={() => theme.dashboard.positive}
            />
          </ProgressLink>
        )}

        {vat && (
          <ProgressLink to={getVATPageUrl(slug, { id: vat.id ?? vatPeriodId })}>
            <DateProgressItem
              appearance="flat"
              initialAnimation
              dateFrom={new Date(vat.start_date)}
              dateTo={new Date(vat.end_date)}
              titleFrom={t('dashboard.periodProgress.vatPeriodStart')}
              titleTo={t('dashboard.periodProgress.vatPeriodEnd')}
              progressColor={() => theme.dashboard.caution}
            />
          </ProgressLink>
        )}
      </Body>
    </FullWidthCard>
  )
}

const FullWidthCard = styled(Card)`
  flex: 1;
  min-width: 0;
`

const Body = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;

  ${WHEN_STACKED} {
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.md}rem;
  }
`

const ProgressLink = styled(Link)`
  flex: 1;
  min-width: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`
